import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { img } from '../lib/content';
import SmartMedia from './SmartMedia';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const toMinutes = (t) => {
  const [h, m] = String(t || '').split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
};

const withinWindow = (start, end, cur) => {
  const s = toMinutes(start);
  const e = toMinutes(end);
  if (s !== null && cur < s) return false;
  if (e !== null && cur > e) return false;
  return true;
};

// All enabled popups configured for today's weekday that are inside their time window.
export const pickPromosForToday = (promos, now = new Date()) => {
  if (!Array.isArray(promos)) return [];
  const today = DAY_NAMES[now.getDay()];
  const cur = now.getHours() * 60 + now.getMinutes();
  return promos.filter(
    (p) =>
      p &&
      p.enabled &&
      String(p.day || '').toLowerCase().trim() === today &&
      withinWindow(p.startTime, p.endTime, cur)
  );
};

const PromoCard = ({ promo, entered, onClose }) => {
  const menuHref = promo.menuFile ? img(promo.menuFile) : promo.buttonUrl;
  return (
    <div
      data-testid="promo-popup-card"
      className={`relative w-full max-w-lg bg-[#f7f1e3] border-2 border-[#942222] rounded-md overflow-hidden shadow-2xl transition-all duration-500 ${entered ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-4 scale-95 opacity-0'}`}
    >
      <button
        onClick={onClose}
        data-testid="promo-popup-close"
        aria-label="Close"
        className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-[#f7f1e3]/90 text-[#942222] border border-[#942222]/30 hover:bg-[#942222] hover:text-[#f1ead9] transition-colors"
      >
        <X size={18} />
      </button>

      {promo.photo && (
        <div className="w-full overflow-hidden border-b-2 border-[#e2b95a] bg-[#efe6d0] flex items-center justify-center">
          <SmartMedia src={promo.photo} alt={promo.title || 'Promotion'} className="w-full h-auto max-h-[60vh] object-contain" controls />
        </div>
      )}

      <div className="p-8 text-center">
        {promo.eyebrow && (
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#b8862f] font-semibold" data-testid="promo-popup-eyebrow">
            {promo.eyebrow}
          </p>
        )}
        {promo.title && (
          <h3 className="mt-2 font-serif text-3xl md:text-4xl text-[#942222]" data-testid="promo-popup-title">
            {promo.title}
          </h3>
        )}
        {promo.body && (
          <p className="mt-3 text-sm text-[#3a2a22] leading-relaxed" data-testid="promo-popup-body">
            {promo.body}
          </p>
        )}
        {promo.buttonLabel && menuHref && (
          <a
            href={menuHref}
            target="_blank"
            rel="noreferrer"
            data-testid="promo-popup-button"
            className="mt-6 inline-block px-7 py-3 bg-[#942222] hover:bg-[#b13030] text-[#f1ead9] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors"
          >
            {promo.buttonLabel}
          </a>
        )}
      </div>
    </div>
  );
};

const PromoPopup = ({ promos }) => {
  const todays = pickPromosForToday(promos); // show every enabled popup matching today
  const seenKey = `mp_promo_seen_day_${new Date().getDay()}`;
  const [seen] = useState(() => {
    try { return !!sessionStorage.getItem(seenKey); } catch { return false; }
  });
  const [dismissed, setDismissed] = useState({});
  const [entered, setEntered] = useState(false);

  const count = todays.length;
  const anyVisible = !seen && count > 0 && todays.some((_, i) => !dismissed[i]);

  useEffect(() => {
    if (seen || count === 0) return;
    try { sessionStorage.setItem(seenKey, '1'); } catch { /* ignore */ }
    const enterTimer = setTimeout(() => setEntered(true), 50);
    const maxSecs = Math.max(
      ...todays.map((p) => {
        const s = parseInt(p.autoDismissSeconds, 10);
        return Number.isNaN(s) || s <= 0 ? 15 : s;
      })
    );
    const dismissTimer = setTimeout(
      () => setDismissed(Object.fromEntries(todays.map((_, i) => [i, true]))),
      maxSecs * 1000
    );
    return () => {
      clearTimeout(enterTimer);
      clearTimeout(dismissTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen, count]);

  if (!anyVisible) return null;

  const closeAll = () => setDismissed(Object.fromEntries(todays.map((_, i) => [i, true])));
  const visible = todays.map((p, i) => ({ p, i })).filter((x) => !dismissed[x.i]);
  const single = visible.length === 1;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-wrap items-center content-center justify-center gap-5 px-4 py-10 overflow-y-auto"
      data-testid="promo-popup"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-[#2a0d0d]/75 backdrop-blur-sm" onClick={closeAll} data-testid="promo-popup-backdrop" />
      {visible.map(({ p, i }) => (
        <div key={i} className={`relative w-full ${single ? 'max-w-lg' : 'sm:w-[360px]'}`}>
          <PromoCard promo={p} entered={entered} onClose={() => setDismissed((d) => ({ ...d, [i]: true }))} />
        </div>
      ))}
    </div>
  );
};

export default PromoPopup;
